import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Save, Loader2, User, BadgeCheck, Phone, Building2, Lock, KeyRound, Sparkles, ArrowUpRight, Receipt, Calendar, ImageDown, MapPin, Trash2, Globe, Camera } from "lucide-react";
import PhotoReportConfigForm from "@/components/photoreport/PhotoReportConfigForm";
import { isPhotoAutoSaveEnabled, setPhotoAutoSaveEnabled } from "@/lib/photoBackup";
import EmailSettings from "@/components/EmailSettings";
import BrandingPreview from "@/components/BrandingPreview";
import SecuritySettings from "@/components/SecuritySettings";
import DevicesSettings from "@/components/DevicesSettings";
import { toast } from "sonner";
import { usePwaUpdate } from "@/lib/PwaUpdateContext";
import { RefreshCw, CheckCircle2, DownloadCloud, WifiOff, AlertTriangle } from "lucide-react";

const STATUS_META = {
  idle: { label: "Up to date", cls: "text-emerald-700", Icon: CheckCircle2 },
  uptodate: { label: "Up to date", cls: "text-emerald-700", Icon: CheckCircle2 },
  checking: { label: "Checking…", cls: "text-slate-600", Icon: RefreshCw },
  downloading: { label: "Downloading update…", cls: "text-indigo-700", Icon: DownloadCloud },
  ready: { label: "Update ready", cls: "text-teal-700", Icon: DownloadCloud },
  offline: { label: "Offline — unable to check", cls: "text-amber-700", Icon: WifiOff },
  error: { label: "Update check failed — retry", cls: "text-red-700", Icon: AlertTriangle },
};

const AppUpdatesCard = () => {
  const { status, updateReady, lastCheck, runningBuild, check, applyUpdate } = usePwaUpdate();
  const [busy, setBusy] = useState(false);
  const view = updateReady ? "ready" : (status || "idle");
  const meta = STATUS_META[view] || STATUS_META.idle;
  const { Icon } = meta;

  return (
    <div className="bg-white border-2 border-slate-200 p-6 space-y-4 mb-6" data-testid="app-updates-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-black text-slate-900">App updates</h2>
          <p className="text-slate-600 text-sm mt-1">Compliance Hub updates automatically. You can also check here anytime.</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${meta.cls}`} data-testid="app-update-status">
          <Icon size={14} className={view === "checking" ? "animate-spin" : ""} /> {meta.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Running build</span>
          <code className="text-slate-800 text-xs" data-testid="app-running-build">{runningBuild}</code></div>
        <div><span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Last checked</span>
          <span className="text-slate-800 text-xs">{lastCheck ? new Date(lastCheck).toLocaleString() : "—"}</span></div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={async () => { setBusy(true); try { await check(true); } finally { setBusy(false); } }}
          disabled={busy}
          data-testid="app-check-updates"
          className="h-10 px-4 inline-flex items-center gap-2 border-2 border-slate-300 font-bold text-xs uppercase tracking-wider hover:border-slate-900 disabled:opacity-60"
        >
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Check for updates
        </button>
        {updateReady && (
          <button
            onClick={async () => { setBusy(true); try { await applyUpdate(); } finally { setBusy(false); } }}
            disabled={busy}
            data-testid="app-update-now"
            className="h-10 px-4 inline-flex items-center gap-2 bg-[#14B8A6] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#0D9488] disabled:opacity-60"
          >
            <DownloadCloud size={14} /> Update now
          </button>
        )}
      </div>
    </div>
  );
};

const Settings = () => {
  const [form, setForm] = useState({ name: "", bdc_number: "", mobile: "", company_name: "", booking_url: "", council_address: "", org_type: "private_certifier" });
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setForm({
          name: r.data.name || "",
          bdc_number: r.data.bdc_number || "",
          mobile: r.data.mobile || "",
          company_name: r.data.company_name || "",
          booking_url: r.data.booking_url || "",
          council_address: r.data.council_address || "",
          org_type: r.data.org_type || "private_certifier",
          abn: r.data.abn || "",
          website: r.data.website || "",
          business_logo: r.data.business_logo || "",
          photo_report_defaults: r.data.photo_report_defaults || {},
        });
        setIsTeamMember(!!r.data.is_team_member);
        setUserEmail(r.data.email || "");
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", form);
      // refresh cached user in localStorage
      const cached = JSON.parse(localStorage.getItem("psc_user") || "null");
      if (cached) localStorage.setItem("psc_user", JSON.stringify({ ...cached, ...r.data }));
      toast.success("Profile updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin"/></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Settings</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Certifier profile</h1>
        <p className="text-slate-600 mt-2">These details appear on every <strong>Notice of Non-Compliance</strong> PDF you generate.</p>
        <a href="/settings/council-emails" data-testid="settings-council-emails-link"
          className="mt-3 inline-flex items-center gap-2 h-11 px-4 border-2 border-[#1E1B4B] text-[#1E1B4B] font-bold text-sm hover:bg-[#1E1B4B] hover:text-white transition-colors">
          Saved Council Emails — NSW Council Directory →
        </a>
      </div>

      <AppUpdatesCard />

      <div className="bg-white border-2 border-slate-200 p-6 space-y-5">
        <Field icon={User} label="Accredited Certifier name" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="settings-name" />
        <Field icon={Building2} label="Company name (optional)" value={form.company_name} onChange={(v) => setForm(f => ({ ...f, company_name: v }))} testid="settings-company" />
        <Field icon={BadgeCheck} label="BDC / Accreditation number" placeholder="e.g. BDC 2801" value={form.bdc_number} onChange={(v) => setForm(f => ({ ...f, bdc_number: v }))} testid="settings-bdc" />
        <Field icon={Phone} label="Mobile" placeholder="e.g. 0413 251 025" value={form.mobile} onChange={(v) => setForm(f => ({ ...f, mobile: v }))} testid="settings-mobile" />
        <Field icon={Calendar} label="Re-inspection booking URL (Calendly / Cal.com — shown on the homeowner portal)" placeholder="https://calendly.com/your-handle/re-inspection" value={form.booking_url} onChange={(v) => setForm(f => ({ ...f, booking_url: v }))} testid="settings-booking-url" />
        {form.org_type === "council" && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 flex items-center gap-1.5">
              <MapPin size={13} /> Council postal address
            </p>
            <textarea value={form.council_address} rows={3}
              onChange={(e) => setForm((f) => ({ ...f, council_address: e.target.value }))}
              placeholder={"PO Box 123\nYour Town NSW 2000"}
              data-testid="settings-council-address"
              className="w-full border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:border-[#1E1B4B]" />
            <p className="text-[11px] text-slate-400 mt-1">Fills the <code className="bg-slate-100 px-1 font-mono">{"{{council_address}}"}</code> placeholder on Notice / Direction letters — e.g. where owners post their representations.</p>
          </div>
        )}
        {!isTeamMember && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Organisation type</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "private_certifier", label: "Private Certifier", hint: "Full workflow incl. notifying council" },
                { v: "council", label: "Council", hint: "Council-notification steps hidden" },
              ].map((o) => (
                <button type="button" key={o.v} onClick={() => setForm((f) => ({ ...f, org_type: o.v }))}
                  data-testid={`settings-org-${o.v}`}
                  className={`text-left border-2 p-3 transition-colors ${form.org_type === o.v ? "border-[#1E1B4B] bg-[#1E1B4B]/5" : "border-slate-200 hover:border-slate-400"}`}>
                  <span className="block font-black text-sm text-slate-900">{o.label}</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">{o.hint}</span>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">Council organisations don&apos;t see &quot;Send to council&quot; steps or council-notification reminders — the statutory workflow completes once certificates are issued.</p>
          </div>
        )}
        <div className="pt-2">
          <button onClick={save} disabled={saving} data-testid="settings-save-button"
            className="h-12 px-6 inline-flex items-center gap-2 bg-[#1E1B4B] text-white font-bold border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16} strokeWidth={2.5}/>}
            Save profile
          </button>
        </div>
      </div>

      {/* Business branding — org logo/details used on THEIR documents & emails */}
      <div className="bg-white border-2 border-slate-200 p-6 space-y-5 mt-6" data-testid="business-branding-card">
        <div>
          <h2 className="text-lg font-black text-slate-900">Business branding</h2>
          <p className="text-sm text-slate-600 mt-1">
            Your logo and details appear on <strong>your</strong> notices, directions, inspection reports and outgoing
            emails — so documents look like they come from your business. The app itself stays Compliance Hub.
          </p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Business logo</p>
          {form.business_logo ? (
            <div className="flex items-center gap-4">
              <div className="light-island border-2 border-slate-200 p-3 rounded-lg">
                <img src={form.business_logo} alt="Business logo" className="h-16 max-w-[220px] object-contain" data-testid="branding-logo-preview" />
              </div>
              <button type="button" data-testid="branding-logo-remove"
                onClick={() => setForm((f) => ({ ...f, business_logo: "" }))}
                className="h-10 px-3 border-2 border-red-300 text-red-700 font-bold text-xs uppercase tracking-wider hover:bg-red-50">
                Remove
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 h-11 px-4 border-2 border-dashed border-slate-300 text-slate-600 font-bold text-sm cursor-pointer hover:border-slate-500">
              Upload logo (PNG/JPG)
              <input type="file" accept="image/*" className="hidden" data-testid="branding-logo-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const dataUrl = await new Promise((res) => {
                    const r = new FileReader();
                    r.onload = () => {
                      const img = new Image();
                      img.onload = () => {
                        const scale = Math.min(1, 480 / Math.max(img.width, img.height));
                        const c = document.createElement("canvas");
                        c.width = Math.round(img.width * scale);
                        c.height = Math.round(img.height * scale);
                        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
                        res(c.toDataURL("image/png"));
                      };
                      img.src = r.result;
                    };
                    r.readAsDataURL(file);
                  });
                  setForm((f) => ({ ...f, business_logo: dataUrl }));
                }} />
            </label>
          )}
          <p className="text-[11px] text-slate-400 mt-1.5">Transparent PNG works best. Shown at the top of your notices and in your email signature.</p>
        </div>
        <Field icon={Building2} label="ABN (optional — shown in your email signature)" placeholder="e.g. 12 345 678 901"
          value={form.abn} onChange={(v) => setForm((f) => ({ ...f, abn: v }))} testid="settings-abn" />
        <Field icon={Globe} label="Website (optional — linked in your email signature)" placeholder="e.g. www.yourbusiness.com.au"
          value={form.website} onChange={(v) => setForm((f) => ({ ...f, website: v }))} testid="settings-website" />
        <BrandingPreview form={form} email={userEmail} />
        <div className="pt-1">
          <button onClick={save} disabled={saving} data-testid="branding-save-button"
            className="h-11 px-5 inline-flex items-center gap-2 bg-[#1E1B4B] text-white font-bold border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin"/> : <Save size={15} strokeWidth={2.5}/>}
            Save branding
          </button>
        </div>
      </div>

      {/* Photo Report defaults — org-wide settings for the Photo Report Template */}
      <div className="bg-white border-2 border-slate-200 p-6 space-y-5 mt-6" data-testid="photo-report-defaults-card">
        <div>
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><Camera size={18}/> Photo Report defaults</h2>
          <p className="text-xs text-slate-500 mt-1">Applied whenever an officer creates a Photo Report Template. A checklist can override these in the Builder&apos;s &quot;Photo report&quot; settings.</p>
        </div>
        <PhotoReportConfigForm
          value={form.photo_report_defaults || {}}
          onChange={(patch) => setForm((f) => ({ ...f, photo_report_defaults: { ...(f.photo_report_defaults || {}), ...patch } }))}
          testid="org-pr-config"/>
        <div className="pt-1">
          <button onClick={save} disabled={saving} data-testid="photo-report-defaults-save-button"
            className="h-11 px-5 inline-flex items-center gap-2 bg-[#1E1B4B] text-white font-bold border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin"/> : <Save size={15} strokeWidth={2.5}/>}
            Save photo report defaults
          </button>
        </div>
      </div>

      <div className="mt-6">
        <EmailSettings />
      </div>
      <div className="mt-6">
        <SecuritySettings />
      </div>
      <DevicesSettings />
      <PhotoBackupPanel />
      <CouncilOverridesPanel />
      <ChangePasswordPanel />
      <BillingPanel />
    </div>
  );
};

const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

const CouncilOverridesPanel = () => {
  const [rows, setRows] = useState(null);
  const [removing, setRemoving] = useState("");
  const [orgType, setOrgType] = useState("private_certifier");

  useEffect(() => {
    api.get("/statutory/config").then((r) => setOrgType(r.data.org_type)).catch(() => {});
    api.get("/council-overrides").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const remove = async (suburb) => {
    setRemoving(suburb);
    try {
      await api.delete(`/council-overrides/${encodeURIComponent(suburb)}`);
      setRows((prev) => prev.filter((r) => r.suburb !== suburb));
      toast.success(`Override removed — ${titleCase(suburb)} will use the directory default again`);
    } catch { toast.error("Could not remove override"); }
    finally { setRemoving(""); }
  };

  if (orgType === "council") return null; // council orgs never email councils

  return (
    <div className="bg-white border-2 border-slate-200 p-6 mt-8 space-y-4" data-testid="council-overrides-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 bg-[#1E1B4B] text-white flex items-center justify-center"><MapPin size={18} strokeWidth={2.5}/></div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Council submissions</p>
          <h3 className="font-black text-slate-900">Saved council emails</h3>
        </div>
      </div>
      <p className="text-sm text-slate-600">
        When you edit a council&apos;s email before sending a Non-Compliance Certificate, the app remembers it per suburb and uses it automatically for future inspections there.
      </p>
      {!rows ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={15} className="animate-spin"/> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400 italic" data-testid="council-overrides-empty">No saved overrides yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="council-overrides-list">
          {rows.map((r) => (
            <li key={r.suburb} className="py-3 flex flex-wrap items-center gap-2" data-testid={`council-override-row-${r.suburb.replace(/\s+/g, "-")}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-900 text-sm">{titleCase(r.suburb)}</p>
                <p className="text-xs text-slate-600 break-all">{r.email}{r.council_name ? <span className="text-slate-400"> · {r.council_name}</span> : null}</p>
                {r.default_email && r.default_email !== r.email && (
                  <p className="text-[11px] text-slate-400 break-all">Directory default: {r.default_email}</p>
                )}
              </div>
              <button
                onClick={() => remove(r.suburb)}
                disabled={removing === r.suburb}
                data-testid={`council-override-delete-${r.suburb.replace(/\s+/g, "-")}`}
                className="h-9 px-3 inline-flex items-center gap-1.5 border-2 border-slate-300 text-xs font-bold uppercase tracking-wider text-slate-700 hover:border-red-500 hover:text-red-600 disabled:opacity-60"
              >
                {removing === r.suburb ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12} strokeWidth={2.5}/>}
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PhotoBackupPanel = () => {
  const [on, setOn] = useState(isPhotoAutoSaveEnabled());
  const toggle = () => {
    const next = !on;
    setOn(next);
    setPhotoAutoSaveEnabled(next);
    toast.success(next ? "Photos will be saved to this device as you take them" : "Photo auto-save turned off");
  };
  return (
    <div className="bg-white border-2 border-slate-200 p-6 mt-8 space-y-4" data-testid="photo-backup-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 bg-[#1E1B4B] text-white flex items-center justify-center"><ImageDown size={18} strokeWidth={2.5}/></div>
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">This device</p>
          <h3 className="font-black text-slate-900">Photo backup</h3>
        </div>
        <button onClick={toggle} data-testid="photo-backup-toggle" role="switch" aria-checked={on}
          className={`relative w-14 h-8 border-2 transition-colors ${on ? "bg-teal-600 border-teal-600" : "bg-slate-200 border-slate-300"}`}>
          <span className={`absolute top-0.5 w-6 h-6 bg-white shadow transition-all ${on ? "left-[26px]" : "left-0.5"}`} />
        </button>
      </div>
      <p className="text-sm text-slate-600">
        When on, every photo you take with the in-app camera is <strong>also saved to this phone&apos;s Downloads folder</strong> (named
        <code className="mx-1 px-1 bg-slate-100 text-xs">PoolSpa_…</code>) — so even if the app ever loses data, your site photos stay on the device.
        Your gallery app shows them under the <strong>Downloads</strong> album. You can also export all photos of any inspection with its <strong>Photos</strong> button.
      </p>
    </div>
  );
};

const TIER_LABELS = {
  free: { label: "Free", color: "bg-slate-100 text-slate-800 border-slate-300" },
  solo: { label: "Solo Inspector", color: "bg-emerald-50 text-emerald-900 border-emerald-300" },
  pro: { label: "Pro Firm", color: "bg-[#1E1B4B] text-white border-[#1E1B4B]" },
  enterprise: { label: "Enterprise", color: "bg-amber-50 text-amber-900 border-amber-300" },
};

const BillingPanel = () => {
  const [me, setMe] = useState(null);
  const [txs, setTxs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [m, t] = await Promise.all([api.get("/billing/me"), api.get("/billing/transactions")]);
        setMe(m.data); setTxs(t.data);
      } catch { /* user may not have billing endpoint access — okay */ }
    })();
  }, []);

  if (!me) return null;
  const tierMeta = TIER_LABELS[me.tier] || TIER_LABELS.free;
  const exp = me.tier_expires_at ? new Date(me.tier_expires_at) : null;

  return (
    <div className="bg-white border-2 border-slate-200 p-6 mt-8 space-y-5" data-testid="billing-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 bg-[#1E1B4B] text-white flex items-center justify-center"><Sparkles size={18} strokeWidth={2.5}/></div>
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Subscription</p>
          <h3 className="font-black text-slate-900">Your plan</h3>
        </div>
        <span data-testid="billing-current-tier" className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border-2 ${tierMeta.color}`}>{tierMeta.label}</span>
      </div>
      {me.tier === "free" ? (
        <>
          <p className="text-sm text-slate-600">You&apos;re on the Free plan. Upgrade to unlock unlimited AI reports, the Checklist Builder, team management and more.</p>
          <Link to="/pricing" data-testid="upgrade-cta" className="h-12 px-6 inline-flex items-center gap-2 bg-[#1E1B4B] border-2 border-[#1E1B4B] text-white font-bold hover:bg-[#0F0E2E]">
            See plans <ArrowUpRight size={16} strokeWidth={2.5}/>
          </Link>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            Your <strong>{tierMeta.label}</strong> plan is active{exp ? <> until <strong className="text-slate-900">{exp.toLocaleDateString()}</strong>.</> : "."} You&apos;ll find your invoices below.
          </p>
          <Link to="/pricing" className="text-xs font-bold text-[#1E1B4B] hover:underline">Change plan →</Link>
        </>
      )}

      {txs.length > 0 && (
        <div className="pt-3 border-t border-slate-100">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1"><Receipt size={11} strokeWidth={3}/> Recent transactions</p>
          <ul className="divide-y divide-slate-100 text-sm" data-testid="billing-transactions">
            {txs.slice(0, 6).map((t) => (
              <li key={t.id} className="py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 truncate">{t.tier?.toUpperCase()} · {t.cadence}</p>
                  <p className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm text-slate-900">A${t.amount.toFixed(2)}</p>
                  <p className={`text-[10px] font-bold uppercase tracking-wider ${t.payment_status === "paid" ? "text-emerald-700" : t.payment_status === "pending" ? "text-amber-700" : "text-red-700"}`}>{t.payment_status}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ChangePasswordPanel = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border-2 border-slate-200 p-6 mt-8 space-y-5" data-testid="change-password-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 bg-[#1E1B4B] text-white flex items-center justify-center"><Lock size={18} strokeWidth={2.5}/></div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Security</p>
          <h3 className="font-black text-slate-900">Change password</h3>
        </div>
      </div>
      <PField label="Current password" value={current} onChange={setCurrent} testid="cp-current" />
      <PField label="New password (min 6 characters)" value={next} onChange={setNext} testid="cp-new" />
      <PField label="Confirm new password" value={confirm} onChange={setConfirm} testid="cp-confirm" />
      <div className="pt-1">
        <button onClick={submit} disabled={saving || !current || !next || !confirm} data-testid="cp-submit"
          className="h-12 px-6 inline-flex items-center gap-2 bg-teal-600 text-white font-bold border-2 border-teal-600 hover:bg-teal-700 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin"/> : <KeyRound size={16} strokeWidth={2.5}/>}
          Update password
        </button>
      </div>
    </div>
  );
};

const PField = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{label}</span>
    <input
      data-testid={testid}
      type="password"
      autoComplete="new-password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-12 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"
    />
  </label>
);

const Field = ({ icon: Icon, label, value, onChange, testid, placeholder }) => (
  <label className="block">
    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
      <Icon size={13} strokeWidth={2.5} />{label}
    </span>
    <input
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-12 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"
    />
  </label>
);

export default Settings;
